import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { default as CloseIcon } from '@material-ui/icons/Close';
import { Alert, AlertTitle } from '@material-ui/lab';
import * as jsYaml from 'js-yaml';
import { DropzoneArea } from 'material-ui-dropzone';
import React, { useContext, useState } from 'react';
import AceEditor from "react-ace";
import UserContext from '../../../auth/UserContext';
import FileUtils from '../../tests/FileUtils';
import { buildEmptyWorkflow } from '../../../common/WorkflowUtils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    closeButton: {
      padding: 4
    },
    dropzoneText: {
      color: theme.palette.type === 'light' ? theme.palette.text.hint : theme.palette.text.secondary
    },
    dialogTitle: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    alert: {
      marginBottom: 24
    },
    filePreviewSection: {
      marginBottom: 16
    },
    filePreview: {
      border: `${theme.palette.divider} solid 1px`
    },
    fileNameChip: {
      marginBottom: 8
    }
  })
);

function parseFile(content: string, type: string) {
  let parsedFile = null;

  if (type === 'application/json') {
    parsedFile = JSON.parse(content);
  } else if (type === 'application/x-yaml') {
    parsedFile = jsYaml.safeLoad(content);
  } else {
    try {
      parsedFile = jsYaml.safeLoad(content);
    } catch (error) {
      parsedFile = JSON.parse(content);
    }
  }
  return parsedFile;
}

function sanitizeWorkflow(wf: any, familyId: string, user: any) {
  // Remove fields from an existing workflow
  delete wf.Versions;
  delete wf.Archived;
  delete wf.GUID;
  delete wf.Family;
  delete wf.LastExecutionTime;
  delete wf.LastExecutionGUID;
  delete wf.WorkflowStatus;
  delete wf.LastModified;
  delete wf.Teardown;
  delete wf.NotificationTopicARN;

  if (!wf.DeploymentRecipe) {
    wf.DeploymentRecipe = [];
  }

  if (!wf.TestFunctions) {
    wf.TestFunctions = [];
  }

  [...wf.DeploymentRecipe, ...wf.TestFunctions].forEach(action => {
    if (!action.Parameters) {
      action.Parameters = {};
    }
    action.Parameters = Object.keys(action.Parameters).reduce((accumulator, key) => {
      const paramValue = action.Parameters[key];
      accumulator[key] = paramValue !== null ? String(action.Parameters[key]) : ''
      return accumulator;
    }, {});
  });

  return {
    ...buildEmptyWorkflow(familyId, user.email),
    ...wf
  }
}

interface Props {
  familyId: string;
  onClose: (workflow?: any) => void;
}

function WorkflowUploadDialog(props: Props) {
  const { familyId, onClose } = props;
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const user = useContext(UserContext);
  const classes = useStyles({});
  const theme = useTheme();

  const onDrop = (files) => {
    if (files.length) {
      new FileUtils().readFileAsString(files[0]).then(fileStr => {
        setFile({
          name: files[0].name,
          type: files[0].type,
          content: fileStr
        });
      }).catch(error => {
        setError(`Failed to load file: ${error}`);
      });
    }
  };

  const onClearFile = () => {
    setFile(null);
    setError(null);
  };

  const onSubmit = () => {
    try {
      const parsedFile = parseFile(file.content, file.type);
      onClose(sanitizeWorkflow(parsedFile, familyId, user));
    } catch (error) {
      setError(`Failed to parse file. File must be a valid worfklow in JSON or YAML format. ${error}`);
    }
  };

  return (
    <Dialog
      open={true}
      scroll="paper"
      onClose={() => onClose()}
      aria-labelledby="workflow-upload-dialog-title"
      aria-describedby="workflow-upload-dialog-description"
      maxWidth="sm"
      fullWidth
      disableBackdropClick
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <div>
          <Typography variant="h6">Load Workflow From File</Typography>
          <Typography variant="caption">JSON and YAML workflow formats are supported</Typography>
        </div>
        <IconButton onClick={() => onClose()} className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="warning" className={classes.alert}>
          <AlertTitle>{error}</AlertTitle>
        </Alert>}
        {!file && <DropzoneArea
          classes={{ icon: classes.dropzoneText }}
          dropzoneClass={classes.dropzoneText}
          dropzoneParagraphClass={classes.dropzoneText}
          acceptedFiles={['.yaml,.yml,.json,.windsor,.workflow']}
          onChange={onDrop}
          filesLimit={1}
          dropzoneText="Drag and drop workflow or click to browse"
          showPreviewsInDropzone={false}
          showAlerts={false}
        />}
        {file && <div className={classes.filePreviewSection}>
          <Chip className={classes.fileNameChip} label={file.name} size="small" variant="outlined" onDelete={onClearFile} />
          <div className={classes.filePreview}>
            <AceEditor
              readOnly
              wrapEnabled
              mode={'text'}
              theme={theme.palette.type === 'light' ? 'github' : 'monokai'}
              width="100%"
              minLines={15}
              maxLines={15}
              value={file.content}
              setOptions={{ useWorker: false, showGutter: false, highlightActiveLine: false }}
              showPrintMargin={false}
              editorProps={{ $blockScrolling: true }}
            />
          </div>
        </div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} color="primary">
          Cancel
        </Button>
        <Button onClick={onSubmit} disabled={!file} color="primary" variant="contained">
          Submit
        </Button>
      </DialogActions>
    </Dialog >
  )
}

export default WorkflowUploadDialog;